/**
 * Which dossier slots can be written on save, and how they map onto LoanUpdate
 * (native columns) vs custom_fields. Display-only surfaces (Walk Score, maps,
 * derived lease expirations) are listed so unlock does not pretend they persist.
 */
import type { DealFieldDef } from '@/services/dealFile'

/** Native / aliased columns on PUT /loans/{id} (LoanUpdate). */
export const LOAN_UPDATE_COLUMNS = new Set([
  'property_name',
  'asset_type',
  'location',
  'borrower',
  'sponsor',
  'sponsor_credit',
  'loan_amount',
  'broker_name',
  'broker_id',
  'ltv',
  'dscr',
  'stage',
  'stage_index',
  'phase',
  'assignee',
  'originator',
  'investor',
  'capital_program',
  'days_open',
  'maturity',
  'rate',
  'originated',
  'priority',
  'cash_flows',
  'market_data',
  'custom_fields',
  'product',
  'property_value',
  'noi',
  'sf',
  'units',
  'year_built',
  'debt_yield',
])

/** Accrues on the deal in custom_fields when there is no native column. */
export const CUSTOM_WRITABLE = new Set([
  'purpose',
  'property_subtype',
  'occupancy',
  'cap_rate',
  'expense_ratio',
  'gross_revenue',
  'operating_expenses',
  'ncf',
  'stories',
  'year_renovated',
  'parcel_id',
  'purchase_price',
  'property_management',
  'property_description',
  'price_per_sf',
  'base_rent_annual',
  'rent_per_sf',
  'underwriter',
  'analyst',
  'sponsor_contact',
  'noi_stabilized',
  'ltc',
  'spread',
  'term_months',
  'maturity_date',
  'street',
  'city',
  'state',
  'zip',
  'website',
  'rent_roll',
  'parking_spaces',
  'avg_lease_term',
  'zoning',
  'county',
  'dscr_stabilized',
  'cap_rate_stabilized',
  'breakeven_occupancy',
  'cash_on_cash',
])

export const DISPLAY_ONLY = new Set([
  'walk_score',
  'photos',
  'map',
  'comparables',
  'lease_expirations',
])

export const WORKSPACE_TO_SNAKE: Record<string, string> = {
  property: 'property_name',
  type: 'asset_type',
  loanAmount: 'loan_amount',
  brokerName: 'broker_name',
  originated: 'originated',
  priority: 'priority',
  assignee: 'assignee',
  location: 'location',
  borrower: 'borrower',
  sponsor: 'sponsor',
  ltv: 'ltv',
  dscr: 'dscr',
  rate: 'rate',
}

export const SNAKE_TO_LOAN: Record<string, string> = {
  property_name: 'property',
  asset_type: 'type',
  loan_amount: 'loanAmount',
  broker_name: 'brokerName',
  property_value: 'propertyValue',
  year_built: 'yearBuilt',
  debt_yield: 'debtYield',
  capital_program: 'product',
}

/**
 * Which dossier slots are governed by a curated reference list, and by which
 * category (`backend/app/core/reference_matching.py` CATEGORIES).
 *
 * This is keyed by FIELD, not set per call site, and `EditableSlot` consults it
 * rather than trusting a `kind` prop. Being governed is a property of the field:
 * a new slot rendering `investor` somewhere else must not be able to reintroduce
 * free text by forgetting to pass `kind="select"`.
 *
 * `broker_firm` is deliberately absent. Brokers are relationship entities with
 * their own directory, resolution flow and merge endpoint — not a flat
 * vocabulary — and `BrokerResolutionCard` already owns that surface.
 *
 * `capital_program` is absent too, and for the opposite reason: it is a CLOSED
 * list released with the platform (`dealfields.PROGRAM_OPTIONS`), which the edit
 * API enforces. A governed field renders as a free-text box with suggestions, so
 * a user could type `Midas Fund II` and be refused on Save; a catalogue select
 * offers exactly the list and marks a legacy value as off it.
 */
export const REFERENCE_CATEGORY_BY_FIELD: Record<string, string> = {
  investor: 'investor',
  originator: 'originator',
  analyst: 'analyst',
  underwriter: 'underwriter',
}

/** The curated category governing this field, or null when it is free text. */
export function referenceCategoryFor(name: string): string | null {
  return REFERENCE_CATEGORY_BY_FIELD[name] ?? null
}

/**
 * `select` is offered for explicitness at a call site, but a governed field
 * renders as a picker whether or not it is passed — see `referenceCategoryFor`.
 */
/** `count`: a whole quantity (square feet, units, spaces) — grouped, no unit. */
export type SlotKind = 'text' | 'number' | 'count' | 'percent' | 'currency' | 'date' | 'select'

export function isWritableField(name: string, defs: DealFieldDef[]): boolean {
  if (DISPLAY_ONLY.has(name)) return false
  if (defs.some((d) => d.name === name)) return true
  return LOAN_UPDATE_COLUMNS.has(name) || CUSTOM_WRITABLE.has(name)
}

/**
 * Identity and system fields: who the deal IS and when the platform recorded
 * it, never a figure a person corrects in place.
 *
 * The Control ID is the key the intake matches every later email to (owner
 * decision record, 11 Aug: YY-NNNN, exact match). A stray keystroke in the
 * header badge would quietly detach the deal from its own correspondence, so
 * no inline surface offers it as an input even while the dossier is unlocked.
 * Ids and timestamps are written by the platform alone; `days_open` and
 * `stage_index` are counters the pipeline keeps, not statements anybody made.
 */
export const SYSTEM_FIELDS: ReadonlySet<string> = new Set([
  'id',
  'loan_id',
  'control_id',
  'controlId',
  'created_at',
  'updated_at',
  'source_ingestion_id',
  'sourceIngestionId',
  'version',
  'days_open',
  'stage_index',
])

export function isSystemField(name: string): boolean {
  return SYSTEM_FIELDS.has(name)
}

export const SYSTEM_FIELD_REASON = 'Set by the platform — an identity or system field, not edited by hand.'

/** The slot kind a catalogue field is edited as, when a caller does not say.
 *  Only the input's keyboard and date handling depend on it: the value itself
 *  is normalized by the field's own catalogue kind (`editableValue`). */
export function slotKindOf(def: Pick<DealFieldDef, 'kind'> | undefined): SlotKind {
  switch (def?.kind) {
    case 'currency':
      return 'currency'
    case 'percent':
      return 'percent'
    case 'date':
      return 'date'
    case 'select':
      return 'select'
    case 'number':
      return 'number'
    default:
      return 'text'
  }
}

/** One field a displayed value is edited as. A value on screen can stand for
 *  more than one field — "Year Built / Reno", an address — and is then edited
 *  as the fields it is made of, never as the composed text.
 *
 *  When the value is edited as two boxes on one line, `separator` stands
 *  between this box and the one before it ('/' when not said; '' for none, as
 *  in "412 Units") and `unit` is printed after it: "Amortization" is a type and
 *  a number of months, and two bare boxes side by side did not say which was
 *  which. */
export type EditTarget = { name: string; label: string; kind?: SlotKind; unit?: string; separator?: string }

/**
 * The address as the fields it is stated in.
 *
 * `location` is composed by the server from these parts on every save, so a
 * typed `location` would be overwritten by the next recomposition. Every
 * surface that prints the address edits these four instead.
 */
export const ADDRESS_EDIT_TARGETS: EditTarget[] = [
  { name: 'street', label: 'Street Address', kind: 'text' },
  { name: 'city', label: 'City', kind: 'text' },
  { name: 'state', label: 'State / Province', kind: 'text' },
  { name: 'zip', label: 'Postal Code', kind: 'text' },
]

/**
 * Why an address is not opened as its parts: the deal file's street and city
 * may be what a Places name lookup wrote, which every read surface withholds.
 * Opened as boxes they would be shown pre-filled as the deal's street, and
 * typing in one asserts the rest. The header's "confirm address" editor never
 * offers that guess, so it is where a street is stated.
 */
export const ADDRESS_CONFIRM_REASON =
  'No street a document or a person stands behind is on record. Confirm the address from the header — the street on file is not shown as the deal’s.'

export function unwritableReason(name: string): string {
  if (name === 'comparables') return 'No comparable set on this record. No column to store comps yet.'
  if (name === 'photos') {
    return 'Deal uploads come from Documents. Places / Street View / the listed website fill the dump when those sources return images.'
  }
  if (name === 'map') return 'Map unavailable until a Google Maps key is in Settings.'
  if (name === 'walk_score') return 'Walk Score is not a stored field and is never invented.'
  return 'No column for this field yet.'
}
